/**
 * Shrink a delivery photo on the phone before it goes up (≤ 2400 px on the long side, JPEG).
 * A 12 MP phone photo is 3-6 MB; at 2400 px it is a few hundred KB, which matters on one bar of
 * signal at a customer's dock. A photo already small enough (a JPEG within the limit) is sent as
 * it is. If the browser can't decode it (HEIC on Android Chrome), the original is sent: the API
 * decides what it accepts.
 */
export const MAX_SIDE = 2400;
export const JPEG_QUALITY = 0.85;

/** The size that fits within maxSide, keeping the shape. Never upscales. */
export function fitWithin(width, height, maxSide = MAX_SIDE) {
  const w = Math.max(0, Number(width) || 0);
  const h = Math.max(0, Number(height) || 0);
  const longest = Math.max(w, h);
  if (!longest || longest <= maxSide) return { width: Math.round(w), height: Math.round(h), scaled: false };
  const s = maxSide / longest;
  return { width: Math.max(1, Math.round(w * s)), height: Math.max(1, Math.round(h * s)), scaled: true };
}

function jpegName(name) {
  const base = String(name || 'photo').replace(/\.[^./\\]+$/, '') || 'photo';
  return `${base}.jpg`;
}

async function decode(file) {
  // createImageBitmap applies the EXIF rotation where asked; else an <img> (browsers rotate it
  // themselves since iOS 13.4 / Chrome 81).
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close && bmp.close() };
    } catch (e) { /* fall back to <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') { resolve(null); return; }
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}

/**
 * -> {blob, filename, content_type, width, height, resized}. Never throws: on any failure the
 * original file comes back (resized: false).
 */
export async function resizePhoto(file, { maxSide = MAX_SIDE, quality = JPEG_QUALITY } = {}) {
  const original = {
    blob: file,
    filename: (file && file.name) || 'photo.jpg',
    content_type: (file && file.type) || 'image/jpeg',
    width: null,
    height: null,
    resized: false,
  };
  if (!file) return original;
  let decoded = null;
  try {
    decoded = await decode(file);
    const fit = fitWithin(decoded.width, decoded.height, maxSide);
    const isJpeg = /^image\/jpe?g$/i.test(file.type || '');
    if (!fit.scaled && isJpeg) return { ...original, width: fit.width, height: fit.height };
    const canvas = document.createElement('canvas');
    canvas.width = fit.width;
    canvas.height = fit.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    ctx.fillStyle = '#fff'; // a transparent PNG becomes white, not black
    ctx.fillRect(0, 0, fit.width, fit.height);
    ctx.drawImage(decoded.source, 0, 0, fit.width, fit.height);
    const blob = await canvasToBlob(canvas, quality);
    canvas.width = 0; // let iOS free the canvas memory at once
    canvas.height = 0;
    if (!blob) return original;
    return { blob, filename: jpegName(file.name), content_type: 'image/jpeg', width: fit.width, height: fit.height, resized: true };
  } catch (e) {
    return original;
  } finally {
    if (decoded) decoded.close();
  }
}
